import React, { useRef, useState } from 'react';
import { Check, ChevronDown, ScanSearch, X } from 'lucide-react';
import { ScanBatch } from '../../types/scan';
import PopoverPortal from '../common/PopoverPortal';
import { SCAN_TYPE_NAMES, formatScanBatchMeta, formatScanBatchTimestamp } from '../scans/scanBatchDisplay';
import { prTerm } from '../../config/providers';

interface ScanFilterProps {
  /** Recent scan batches for the application, newest first (as the scans list returns them). */
  batches: ScanBatch[];
  /** Selected `scan_batch_id`s; empty means the list spans every scan. */
  value: string[];
  /** Called with the new selection — empty to clear the filter. */
  onChange: (scanBatchIds: string[]) => void;
}

const ROW_CLASS = 'flex w-full items-start gap-2 px-3 py-2 text-left transition-colors hover:bg-[var(--surface-2)]';

const scanTypeName = (batch: ScanBatch) => SCAN_TYPE_NAMES[batch.scan_type] || 'Security Scan';

/**
 * Scan filter for the findings list: shows which scans the findings are scoped to and
 * narrows to one scan or several (the union of their findings). Deep links from the scans
 * list arrive as `scan_batch_id` params, so the selection reads back from the trigger and
 * clears in one click instead of being invisible URL state.
 */
const ScanFilter: React.FC<ScanFilterProps> = ({ batches, value, onChange }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const isFiltered = value.length > 0;
  const soleBatch = value.length === 1 ? batches.find((batch) => batch.id === value[0]) : undefined;
  // One scan reads as itself; several read as a count. A deep link to a scan outside the
  // fetched window has no batch to name, so it falls back to a neutral label.
  const label = !isFiltered
    ? 'All scans'
    : value.length > 1
    ? `${value.length} scans`
    : soleBatch
    ? scanTypeName(soleBatch)
    : 'Selected scan';

  const clear = () => {
    onChange([]);
    setIsOpen(false);
  };

  return (
    <div
      ref={anchorRef}
      className={`inline-flex items-stretch overflow-hidden rounded-[var(--radius-sm)] border transition-colors ${
        isFiltered
          ? 'border-[color:var(--lime-soft-border)] bg-[var(--lime-soft)]'
          : 'border-[color:var(--border-subtle)] bg-[var(--surface-2)] hover:border-[color:var(--border)]'
      }`}
    >
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label="Filter by scan"
        onClick={() => setIsOpen(!isOpen)}
        className="flex min-w-0 max-w-[15rem] items-center gap-2 px-2.5 py-1.5 sm:max-w-[22rem]"
      >
        <ScanSearch
          className={`h-3.5 w-3.5 flex-shrink-0 ${
            isFiltered ? 'text-[color:var(--accent-ink)]' : 'text-[color:var(--text-muted)]'
          }`}
        />
        <span className="flex-shrink-0 text-[length:var(--fs-xs)] text-[color:var(--text-muted)]">Scan</span>
        <span
          className={`truncate text-[length:var(--fs-sm)] font-medium ${
            isFiltered ? 'text-[color:var(--text-primary)]' : 'text-[color:var(--text-secondary)]'
          }`}
        >
          {label}
        </span>
        {soleBatch && (
          <span className="hidden flex-shrink-0 font-mono text-[length:var(--fs-2xs)] text-[color:var(--text-muted)] sm:inline">
            {formatScanBatchTimestamp(soleBatch.created_at)}
          </span>
        )}
        <ChevronDown
          className={`h-3.5 w-3.5 flex-shrink-0 text-[color:var(--text-muted)] transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isFiltered && (
        <button
          type="button"
          aria-label="Clear scan filter"
          title="Show findings from every scan"
          onClick={clear}
          className="flex items-center border-l border-[color:var(--lime-soft-border)] px-2 text-[color:var(--text-muted)] transition-colors hover:bg-[var(--surface-3)] hover:text-[color:var(--text-primary)]"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      <PopoverPortal
        anchorRef={anchorRef}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        align="end"
        width={320}
        gap={6}
        className="max-h-[22rem] overflow-y-auto rounded-[var(--radius-md)] border border-[color:var(--border)] bg-[var(--bg-overlay)] py-1.5 shadow-[var(--shadow-menu)]"
      >
        <button
          type="button"
          aria-pressed={!isFiltered}
          onClick={clear}
          className={`${ROW_CLASS} items-center border-b border-[color:var(--divider)] text-[length:var(--fs-sm)] font-medium text-[color:var(--text-body)]`}
        >
          <span className="flex-1">All scans</span>
          {!isFiltered && <Check className="h-3.5 w-3.5 text-[color:var(--accent-ink)]" />}
        </button>

        {/* Toggling leaves the list open, so several scans can be picked in one visit. */}
        {batches.map((batch) => {
          const isSelected = value.includes(batch.id);
          const prNumber = batch.metadata?.pr_number;
          return (
            <button
              key={batch.id}
              type="button"
              aria-pressed={isSelected}
              onClick={() =>
                onChange(isSelected ? value.filter((id) => id !== batch.id) : [...value, batch.id])
              }
              className={ROW_CLASS}
            >
              <span
                aria-hidden="true"
                className={`mt-0.5 flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-[3px] border ${
                  isSelected
                    ? 'border-[color:var(--lime-soft-border)] bg-[var(--lime-soft)] text-[color:var(--accent-ink)]'
                    : 'border-[color:var(--border-strong)]'
                }`}
              >
                {isSelected && <Check className="h-2.5 w-2.5" strokeWidth={3.5} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span
                    className={`truncate text-[length:var(--fs-sm)] ${
                      isSelected
                        ? 'font-semibold text-[color:var(--text-primary)]'
                        : 'font-medium text-[color:var(--text-body)]'
                    }`}
                  >
                    {scanTypeName(batch)}
                  </span>
                  {prNumber && (
                    <span className="flex-shrink-0 rounded-[var(--radius-xs)] bg-[var(--surface-3)] px-1.5 py-px font-mono text-[length:var(--fs-2xs)] text-[color:var(--text-secondary)]">
                      {prTerm(batch.metadata?.provider)} #{prNumber}
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block truncate font-mono text-[length:var(--fs-2xs)] text-[color:var(--text-muted)]">
                  {formatScanBatchMeta(batch)}
                </span>
              </span>
            </button>
          );
        })}
      </PopoverPortal>
    </div>
  );
};

export default ScanFilter;
